import { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ScrollArea } from './ui/scroll-area';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AddLessonModal({ studentId, skills, onClose, onSuccess }) {
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
    const [time, setTime] = useState('09:00');
    const [duration, setDuration] = useState('60');
    const [notes, setNotes] = useState('');
    const [assessments, setAssessments] = useState({});
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    // Time slots from 08:00 to 19:00
    const timeSlots = [];
    for (let h = 8; h <= 19; h++) {
        timeSlots.push(`${h.toString().padStart(2, '0')}:00`);
        if (h < 19) timeSlots.push(`${h.toString().padStart(2, '0')}:30`);
    }

    const categories = [...new Set(skills.map(s => s.category))];
    const categoryNames = {
        bediening_voertuig: 'Bediening Voertuig',
        verkeersdeelneming: 'Verkeersdeelneming',
        bijzondere_verrichtingen: 'Bijzondere Verrichtingen'
    };

    const handleScoreChange = (skillId, score) => {
        setAssessments(prev => {
            if (score === 0) {
                const { [skillId]: removed, ...rest } = prev;
                return rest;
            }
            return { ...prev, [skillId]: { skill_id: skillId, score, notes: '' } };
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            await axios.post(`${API}/lessons`, {
                student_id: studentId,
                date,
                time,
                duration_minutes: parseInt(duration),
                notes,
                assessments: Object.values(assessments)
            }, { withCredentials: true });
            onSuccess();
        } catch (err) {
            setError(err.response?.data?.detail || 'Er is iets misgegaan');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={true} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-2xl bg-[#2D3748] border-[#4A5568] text-white max-h-[90vh]">
                <DialogHeader>
                    <DialogTitle className="text-xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Nieuwe Les Registreren
                    </DialogTitle>
                </DialogHeader>

                {error && (
                    <div className="p-3 bg-red-900/50 border border-red-700 rounded-md text-red-300 text-sm">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <ScrollArea className="h-[60vh] pr-4">
                        <div className="space-y-6">
                            {/* Basic Info */}
                            <div className="grid grid-cols-3 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="lesson-date" className="text-gray-300">Datum</Label>
                                    <Input
                                        id="lesson-date"
                                        type="date"
                                        value={date}
                                        onChange={(e) => setDate(e.target.value)}
                                        required
                                        className="bg-[#1A202C] border-[#4A5568] text-white"
                                        data-testid="lesson-date-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-gray-300">Tijd</Label>
                                    <Select value={time} onValueChange={setTime}>
                                        <SelectTrigger className="bg-[#1A202C] border-[#4A5568] text-white" data-testid="lesson-time-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="bg-[#2D3748] border-[#4A5568] max-h-60">
                                            {timeSlots.map(t => (
                                                <SelectItem key={t} value={t} className="text-white hover:bg-[#3D4A5C]">{t}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-gray-300">Duur</Label>
                                    <Select value={duration} onValueChange={setDuration}>
                                        <SelectTrigger className="bg-[#1A202C] border-[#4A5568] text-white" data-testid="lesson-duration-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="bg-[#2D3748] border-[#4A5568]">
                                            <SelectItem value="45" className="text-white hover:bg-[#3D4A5C]">45 min</SelectItem>
                                            <SelectItem value="60" className="text-white hover:bg-[#3D4A5C]">60 min</SelectItem>
                                            <SelectItem value="90" className="text-white hover:bg-[#3D4A5C]">90 min</SelectItem>
                                            <SelectItem value="120" className="text-white hover:bg-[#3D4A5C]">120 min</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="lesson-notes" className="text-gray-300">Lesnotities</Label>
                                <Textarea
                                    id="lesson-notes"
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    placeholder="Algemene opmerkingen over de les..."
                                    rows={3}
                                    className="bg-[#1A202C] border-[#4A5568] text-white placeholder:text-gray-500"
                                    data-testid="lesson-notes-input"
                                />
                            </div>

                            {/* Skills Assessment */}
                            <div>
                                <Label className="text-base font-semibold mb-4 block text-white">
                                    Beoordeling per vaardigheid
                                </Label>
                                <p className="text-sm text-gray-400 mb-4">
                                    Selecteer alleen de vaardigheden die tijdens deze les zijn geoefend
                                </p>

                                {categories.map(category => {
                                    const categorySkills = skills.filter(s => s.category === category);
                                    
                                    return (
                                        <div key={category} className="mb-6">
                                            <h4 className="font-medium text-[#D4A537] mb-3 text-sm uppercase tracking-wide">
                                                {categoryNames[category] || category}
                                            </h4>
                                            <div className="space-y-2">
                                                {categorySkills.map(skill => {
                                                    const currentScore = assessments[skill.id]?.score || 0;
                                                    
                                                    return (
                                                        <div 
                                                            key={skill.id}
                                                            className="flex items-center justify-between p-3 bg-[#1A202C] rounded-lg border border-[#4A5568]"
                                                        >
                                                            <div className="flex-1">
                                                                <div className="font-medium text-white">{skill.name}</div>
                                                                <div className="text-xs text-gray-500">{skill.description}</div>
                                                            </div>
                                                            <div className="flex items-center gap-1 ml-4">
                                                                {[1, 2, 3, 4, 5].map(score => (
                                                                    <button
                                                                        key={score}
                                                                        type="button"
                                                                        onClick={() => handleScoreChange(skill.id, currentScore === score ? 0 : score)}
                                                                        className={`w-8 h-8 rounded-md text-sm font-bold transition-all ${
                                                                            currentScore === score
                                                                                ? score <= 2
                                                                                    ? 'bg-red-500 text-white'
                                                                                    : score === 3
                                                                                        ? 'bg-amber-500 text-white'
                                                                                        : 'bg-emerald-500 text-white'
                                                                                : 'bg-[#2D3748] border border-[#4A5568] text-gray-400 hover:border-[#D4A537]'
                                                                        }`}
                                                                        data-testid={`skill-score-${skill.id}-${score}`}
                                                                    >
                                                                        {score}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </ScrollArea>

                    <div className="flex justify-between items-center pt-4 border-t border-[#4A5568] mt-4">
                        <div className="text-sm text-gray-400">
                            {Object.keys(assessments).length} vaardigheden beoordeeld
                        </div>
                        <div className="flex gap-3">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={onClose}
                                className="border-[#4A5568] text-gray-300 hover:bg-[#3D4A5C]"
                                data-testid="lesson-cancel-button"
                            >
                                Annuleren
                            </Button>
                            <Button
                                type="submit"
                                disabled={loading}
                                className="bg-[#D4A537] text-[#1A202C] hover:bg-[#E5B84A] font-semibold"
                                data-testid="lesson-submit-button"
                            >
                                {loading ? 'Bezig...' : 'Les Opslaan'}
                            </Button>
                        </div>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}
